import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import React, { useState } from 'react';
import { useCreateAuthority } from '../hooks';
import { useAlert } from '../../context/AlertContext';
import type { IAuthority } from '../../Interfaces/IAuthority';
import type { IFederalState } from '../../Interfaces/IAemter';

interface IProps {
  authorities: IAuthority[];
  federalStates: IFederalState[];
  onClose: () => void;
  onCreated: () => void;
}

/** Neue Behörde (#1208) anlegen – Name + Bundesland + optionale Kontaktdaten. */
export const AddAuthorityDialog: React.FC<IProps> = ({
  authorities,
  federalStates,
  onClose,
  onCreated,
}) => {
  const { showAlert } = useAlert();
  const { createAuthority, isPendingCreateAuthority } = useCreateAuthority();

  const [name, setName] = useState('');
  const [idFederalState, setIdFederalState] = useState<number | ''>('');
  const [email, setEmail] = useState('');
  const [street, setStreet] = useState('');
  const [streetNr, setStreetNr] = useState('');

  const isDuplicate = authorities.some(
    a =>
      a.Authority.trim().toLowerCase() === name.trim().toLowerCase() &&
      a.idFederalState === idFederalState
  );

  const canSave =
    name.trim().length > 0 &&
    typeof idFederalState === 'number' &&
    !isDuplicate &&
    !isPendingCreateAuthority;

  const handleSave = async () => {
    if (!canSave || typeof idFederalState !== 'number') return;
    try {
      await createAuthority({
        idAuthority: -1,
        Authority: name.trim(),
        idFederalState,
        FederalState:
          federalStates.find(f => f.idFederalState === idFederalState)
            ?.FederalState ?? '',
        Authority_Email: email.trim(),
        Street: street.trim(),
        StreetNr: streetNr.trim(),
        activeAuthority: true,
      });
      showAlert({ text: 'Behörde angelegt' });
      onCreated();
      onClose();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Speichern' });
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Neue Behörde</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label='Name der Behörde'
            value={name}
            onChange={e => setName(e.target.value)}
            error={isDuplicate}
            helperText={
              isDuplicate
                ? 'Diese Behörde existiert in diesem Bundesland bereits.'
                : undefined
            }
            autoFocus
            fullWidth
          />
          <TextField
            select
            label='Bundesland'
            value={idFederalState === '' ? '' : String(idFederalState)}
            onChange={e => setIdFederalState(Number(e.target.value))}
            fullWidth
          >
            {federalStates
              .slice()
              .sort((a, b) => a.FederalState.localeCompare(b.FederalState))
              .map(fs => (
                <MenuItem key={fs.idFederalState} value={fs.idFederalState}>
                  {fs.FederalState}
                </MenuItem>
              ))}
          </TextField>
          <Stack direction='row' spacing={2}>
            <TextField
              label='Straße'
              value={street}
              onChange={e => setStreet(e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              label='Nr.'
              value={streetNr}
              onChange={e => setStreetNr(e.target.value)}
              sx={{ width: 100 }}
            />
          </Stack>
          <TextField
            label='E-Mail'
            value={email}
            onChange={e => setEmail(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbruch</Button>
        <Button
          variant='contained'
          onClick={() => void handleSave()}
          disabled={!canSave}
        >
          {isPendingCreateAuthority ? (
            <CircularProgress size={18} />
          ) : (
            'Anlegen'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
